import * as React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { RecordCellSet } from '@/domain/journal/helper';
import {
  valueNormalize as defaultValueNormalize,
  filterList as defaultFilterList,
  localeSort,
  testOption,
} from '@/components/Tables/Cells/ListCell/helpers';

import { getJEListAction } from '@/domain/journal';
import { documentDocumentIdSelector } from '@/domain/documents';

import elements from '@/components/elements';
import AutocompleteOptions from '@/components/Tables/Cells/ListCell/options';
import withManageListItemModal from '@/components/Tables/Cells/ListCell/withManageListItemModal';
import { ListCellWrapper, FieldWrapper, FieldInput } from '@/components/Tables/Cells/ListCell/StyledComponents';
import { CellToggleButton } from '@/components/Tables/Cells/Cell/StyledComponents';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

class Autocomplete extends React.Component {
  static defaultProps = {
    valueNormalize: defaultValueNormalize,
    filterList: defaultFilterList,
    blurTimeOut: 100,
  };

  state = {
    term: '',
    index: -1,
    isDirectionUP: undefined, // eslint-disable-line react/no-unused-state
    isLoading: false,
  };

  componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  onBlur = () => {
    const { index } = this.state;
    // never ever change this without good reason.
    // if wrong index is provided, doSelect will fire and cause sideffects

    this.focused = false;
    this.timeout = setTimeout(() => {
      if (!this.focused) {
        if (index && index >= 1) this.doSelect(index);
        this.props.input.onToggleOpen(false);
        this.props.input.onBlur();
      }
    }, this.props.blurTimeOut);
  };

  onFocus = () => {
    this.focused = true;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = undefined;
    }
  };

  onCreate = (cb) =>
    this.props
      .onCreate()
      .then((options) => {
        if (options) {
          // we must send request with empty value for create
          const set = RecordCellSet({ value: '', options });
          this.props.input.onCreate(set, cb);
        }
      })
      .catch(() => {
        if (this.input) this.input.focus();
        this.slected = false;
      });

  onEdit = (optionItem, cb) =>
    this.props
      .onEdit(optionItem.get('value'))
      .then((options) => {
        const set = RecordCellSet({
          value: optionItem.get('value'),
          display: optionItem.get('display'),
          options,
        });
        this.props.input.onCreate(set, cb);
      })
      .catch(() => {
        if (this.input) this.input.focus();
        this.slected = false;
      });

  onDropCreate = async (dndValue) => {
    const optionList = this.getOptionList();
    const testDndValue = testOption(dndValue);
    const list = optionList.filter(testDndValue(false));
    const results = optionList.findIndex(testDndValue(true));

    if (this.isCreateAvailable) {
      if (results < 0) {
        // if there is no exact match, we show list of submatches found,
        // or open create dialog if mo matches
        if (list.size > 0) {
          await this.setValue(dndValue, 0);
          this.props.input.onToggleOpen(true);
        } else {
          await this.setValue(dndValue);
          this.onCreate(this.cbEnter);
        }
        // this.props.input.onToggleOpen(true);
      } else if (list.size === 1) {
        this.doSelect(results, this.cbEnter);
      } else {
        await this.setValue(dndValue, 0);
        this.props.input.onToggleOpen(true);
      }
    } else if (list.size) {
      if (results > -1) {
        this.doSelect(results, this.cbEnter);
      } else if (list.size) {
        await this.setValue(dndValue, 0);
        this.props.input.onToggleOpen(true);
      }
    }
  };

  onChange = (value, cb = (x) => x) => {
    const {
      optionList,
      input: { onChange },
    } = this.props;
    const item = optionList.filter((f) => f.value === value).first();
    const { display } = item || { display: '' };
    onChange({ target: { value, display } }, cb);
  };

  setValue = (term, index = -1) =>
    new Promise((resolve) => {
      this.setState({ term, index }, () => {
        resolve();
      });
    });

  get dafaultIndex() {
    const { input } = this.props;
    if (input.value.value === null) return -1;
    return this.defaultOptionList.findIndex((e) => e.id === input.value.value);
  }

  get term() {
    const { optionList, input } = this.props;
    if (input.value.value) {
      const value = optionList.filter((f) => f.id === input.value.value).first();
      this.slected = true;
      if (typeof value === 'string') return this.props.valueNormalize(value);
    }
    return '';
  }

  get defaultOptionList() {
    return this.props.optionList.sort(localeSort);
  }

  get value() {
    const {
      state: { term },
    } = this;
    if (term) return term;
    return this.term;
  }

  get isCreateAvailable() {
    return this.props.input.isCreatable;
  }

  getOptionList = () => {
    const { optionList } = this.props;
    const { term } = this.state;
    return term ? this.filterSearchList(optionList, term) : optionList;
  };

  setActive = (index) => {
    if (this.state.index !== index) {
      this.setState({ index }, () => {
        if (index >= 0) {
          this.scrollTo(index);
        }
      });
    }
  };

  setDirection = (isUp) => {
    this.setState({
      isDirectionUP: isUp, // eslint-disable-line react/no-unused-state
    });
  };

  getAllHandledRows = () => [...this.completePages, ...this.loadingPages];

  keyDown = (e) => {
    const { index } = this.state;
    const optionList = this.getOptionList();

    if (e.keyCode === 38) {
      const overlapIndex = optionList.size - 1;
      this.setActive(index > 0 ? index - 1 : overlapIndex);
    }
    if (e.keyCode === 40) {
      const overlapIndex = 0;
      this.setActive(optionList.size > index + 1 ? index + 1 : overlapIndex);
    }
    if (e.keyCode === 27) {
      this.props.input.onToggleOpen(false);
      this.props.input.onKeyDown({ keyCode: 27 });
    }
    if (e.keyCode === 13) {
      e.preventDefault();
      this.doSelect(index, this.cbEnter);
    }
    if (e.keyCode === 8) {
      if (this.slected) {
        this.onChange('');
        this.slected = false;
      }
    }
    if (e.keyCode === 9) {
      // TAB
      e.preventDefault();
      this.doSelect(index, this.cbEnter);
    }
  };

  filterSearchList = (data, term) => {
    const testValue = testOption(term);
    return data.filter(testValue(false));
  };

  doSelect = (index, cb) => {
    const item = this.getOptionList().get(index);

    if (item && !item.get('isActive')) return;

    if (index === -1) {
      if (this.state.term) {
        this.onCreate(cb);
      } else {
        const value = this.props.input.value.value || null;
        // if nothing was selected and input value exist, update with existing value
        // if nothing was selected but value was deleted - update with null
        // to delete on backend
        this.onChange(value, cb);
      }
    } else if (item) {
      this.onChange(item.value, cb);
    }
    this.slected = true;
    this.props.input.onToggleOpen(false);
  };

  cbEnter = () => this.props.input.onKeyDown({ keyCode: 13 });

  refsProxy = (el) => {
    this.input = el;
    this.props.getRefs(el, this);
  };

  searchHandler = (e) => {
    const { value } = e.currentTarget;
    this.props.input.onToggleOpen(true);
    // if we have search results to display set index to 0. 0 means we select first from a list
    // if no search results, -1 must remain caz it allows for onCreate to be invoked
    // on enter key event
    const selectedIndex = this.getOptionList().size ? 0 : -1;
    this.setValue(value, selectedIndex);
  };

  scrollTo = (index) => {
    const shiftVLIndex = this.isCreateAvailable ? index + 1 : index;
    if (this.listRef && this.listRef.current) {
      this.listRef.current.scrollToItem(shiftVLIndex, 'smart');
    }
  };

  handleClose = () => {
    this.props.input.onToggleOpen(false);
  };

  handleOpen = () => {
    this.props.input.onToggleOpen(true);
  };

  handleToggle = () => {
    const { isOpen } = this.props;
    const fn = isOpen ? this.handleClose : this.handleOpen;
    fn();
  };

  slected = false;

  input;

  focused;

  timeout;

  listRef = React.createRef();

  render() {
    const {
      style,
      name,
      pageItemsCount,
      rtl,
      isOpen,
      onComplete,
      valueNormalize,
      id,
      placeholder,
      disabled,
      input,
      children,
    } = this.props;
    const { index, term, isLoading } = this.state;
    return (
      <ListCellWrapper
        tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        onFocus={this.onFocus}
        onBlur={this.onBlur}
        onKeyDown={this.keyDown}
        role="combobox"
        aria-expanded={isOpen}
        aria-controls={id}
        data-element={elements.je.autocomplete.container}
        data-element-id={name.name}
      >
        <FieldWrapper>
          <FieldInput
            id={id}
            aria-autocomplete="list"
            autoComplete="no"
            placeholder={placeholder}
            name={name._cell}
            value={this.value}
            disabled={disabled}
            ref={this.refsProxy}
            onChange={this.searchHandler}
            onDoubleClick={() => input.onToggleOpen(true)}
            style={style}
            data-element={elements.je.autocomplete.field}
            data-element-id={`${name._cell}-${name.name}`}
          />
        </FieldWrapper>
        {isOpen && this.input ? (
          <AutocompleteOptions
            inputEl={this.input}
            rtl={rtl}
            optionList={this.getOptionList()}
            doSelect={(e) => this.doSelect(e, onComplete)}
            valueNormalize={valueNormalize}
            index={index}
            term={term}
            setDirection={this.setDirection}
            isLoading={isLoading}
            pageItemsCount={pageItemsCount}
            itemHeight={30}
            setListRef={this.listRef}
            setActive={this.setActive}
            onClickEdit={(item) => this.onEdit(item, this.cbEnter)}
            addNewItem={this.isCreateAvailable ? () => this.onCreate(this.cbEnter) : null}
          />
        ) : null}
        <CellToggleButton onClick={this.handleToggle} data-element={elements.je.autocomplete.shield}>
          {isOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </CellToggleButton>
        {children(this.value, this.searchHandler)}
      </ListCellWrapper>
    );
  }
}

const mapStateToProps = (state) => ({
  documentID: documentDocumentIdSelector(state),
});

const mapDispatchToProps = {
  getList: getJEListAction,
};

export { Autocomplete as AC };

export default compose(connect(mapStateToProps, mapDispatchToProps), withManageListItemModal)(Autocomplete);
